import { createApp, ref } from 'vue'
import { createPinia } from 'pinia'
import FloatingVue from 'floating-vue'
import * as Sentry from '@sentry/vue'
import { createTokenStorage } from '@space/shared-api-client'

import 'material-design-iconic-font/dist/css/material-design-iconic-font.min.css'
import 'floating-vue/dist/style.css'
import App from './App.vue'
import router from './router'
import matrixStore from '@/modules/matrix/store'
import i18n, { loadLocale, getCurrentLocale } from './i18n'

import './styles/main.scss'
import { initTheme } from '@/composables/useTheme'
import { apiClientPlugin } from '@/plugins/apiClient'
import { notificationsPlugin } from '@/plugins/notifications'

import type { TmaState } from '@/mini-app'
import { createTmaAuthFlow } from '@/mini-app/tmaAuthFlow'
import { hasTelegramLaunchParams, readTelegramInitData } from '@/mini-app/telegramLaunchParams'
import { tmaAddBreadcrumb } from '@/mini-app/tmaDebugLogger'

// Global type declarations for skeleton timeout set in index.html
declare global {
  interface Window {
    __appLoadTimeout?: ReturnType<typeof setTimeout>
    __mascotBootLoader?: { complete: () => void; stop: () => void }
    __skeletonI18n?: { t: Record<string, string>; locale: string }
    __BUILD_VERSION__?: string
  }
}

/**
 * Cache busting: detect when browser has stale HTML that references old assets
 * This happens when CDN/browser caches old HTML but new assets are deployed
 * Solution: catch the error and force a hard reload to get fresh HTML
 * 
 * NOTE: Only enabled in production. In dev mode, these errors usually mean
 * the dev server needs to be restarted (Vite's "Outdated Optimize Dep")
 */
if (import.meta.env.PROD) {
  const RELOAD_ATTEMPTED_KEY = 'gonkagame_reload_attempted'

  const hasReloadBeenAttempted = (): boolean => {
    try {
      return sessionStorage.getItem(RELOAD_ATTEMPTED_KEY) === 'true'
    } catch {
      return false
    }
  }

  const markReloadAttempted = (): void => {
    try {
      sessionStorage.setItem(RELOAD_ATTEMPTED_KEY, 'true')
    } catch {
      // Ignore storage errors
    }
  }

  const clearReloadAttempted = (): void => {
    try {
      sessionStorage.removeItem(RELOAD_ATTEMPTED_KEY)
    } catch {
      // Ignore storage errors
    }
  }

  // Handle chunk/asset load errors (stale cache)
  window.addEventListener('error', (event) => {
    const target = event.target as HTMLElement | null

    // Optional external SDKs are not versioned application chunks. A blocked
    // support widget must not reload the Mini App after a completed login.
    if (target instanceof HTMLScriptElement && (
      target.src === 'https://telegram.org/js/telegram-web-app.js'
      || target.src.startsWith('https://widget.intercom.io/')
    )) return

    // Check if this is a script or link (CSS) load error
    if (target?.tagName === 'SCRIPT' || target?.tagName === 'LINK') {
      console.warn('[GonkaGame] Asset load failed, likely stale cache:', target)

      if (!hasReloadBeenAttempted()) {
        markReloadAttempted()
        console.log('[GonkaGame] Forcing hard reload to fetch fresh assets...')
        window.location.reload()
      }
    }
  }, true) // Use capture phase to catch errors before they bubble

  // Handle unhandled promise rejections (e.g., dynamic import failures)
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason

    // Check if this is a chunk load error (CSS preload or JS dynamic import)
    const isChunkLoadError = reason?.message?.includes('Unable to preload CSS') ||
      reason?.message?.includes('Failed to fetch dynamically imported module') ||
      reason?.message?.includes('Loading chunk') ||
      reason?.message?.includes('Loading CSS chunk')

    if (isChunkLoadError) {
      console.warn('[GonkaGame] Chunk load failed, likely stale cache:', reason?.message)

      if (!hasReloadBeenAttempted()) {
        markReloadAttempted()
        console.log('[GonkaGame] Forcing hard reload to fetch fresh assets...')
        event.preventDefault() // Prevent error from propagating
        window.location.reload()
      }
    }
  })

  // Clear the reload flag once app successfully loads
  // This allows future deploys to trigger reload again
  window.addEventListener('load', () => {
    // Wait a bit to ensure all chunks loaded successfully
    setTimeout(clearReloadAttempted, 5000)
  })
} else {
  // Development mode: just log helpful message instead of reloading
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason

    if (reason?.message?.includes('Outdated Optimize Dep') ||
      reason?.message?.includes('Failed to fetch dynamically imported module')) {
      console.error(
        '%c[GonkaGame DEV] Vite dependency optimization is outdated. Please restart dev server: npm run dev',
        'color: #ff6b6b; font-weight: bold; font-size: 14px;'
      )
    }
  })
}


// Sync runtime theme state (the inline head script already applied data-theme
// before first paint; this attaches the OS listener and seeds reactive state).
initTheme()

const app = createApp(App)

// Capture launch data before installing the router: navigation may remove the hash.
const hasTelegramLaunch = hasTelegramLaunchParams()
const telegramInitData = readTelegramInitData(window.Telegram?.WebApp?.initData)
const isInTelegram = telegramInitData !== null || hasTelegramLaunch

if (isInTelegram) {
  // A remembered browser session must not select an account instead of Telegram.
  createTokenStorage({ storageKey: import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken' }).setToken('')
}

app
  .use(createPinia())
  .use(matrixStore)
  .use(i18n)
  .use(apiClientPlugin as any)
  .use(notificationsPlugin)
  .use(FloatingVue)
  .use(router)

const sentryDsn = import.meta.env.VITE_GONKAGAME_SENTRY_DSN

// Initialize Sentry only when GonkaGame explicitly configures a DSN.
if (import.meta.env.PROD && sentryDsn && !isInTelegram) {
  Sentry.init({
    app,
    dsn: sentryDsn,
    // Release version for tracking errors by build  
    release: `gonkagame@${import.meta.env.VITE_BUILD_VERSION || 'dev'}`,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration()
    ],
    // Ignore external errors from Telegram WebApp API (when users open site via Telegram links)
    // and iziToast internal race conditions
    ignoreErrors: [
      /Method not found/i,
      /Error invoking/i,
      /postEvent/i,
      // iziToast internal errors (race conditions when toasts are removed during animation)
      /r\.children\[m\]\[h\]/i,
      /iziToast/i,
      // ResizeObserver errors (browser internal, not actionable)
      /ResizeObserver loop/i,
      // GeeTest CAPTCHA network errors (third-party service, user connectivity issues)
      /网络错误/i,  // "Network error" in Chinese
      /gt4\.js/i,
      // Chunk load errors (stale cache, handled by auto-reload in main.ts)
      /Unable to preload CSS/i,
      /Failed to fetch dynamically imported module/i,
      /Loading chunk/i,
      /Loading CSS chunk/i
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false
  })
}

/**
 * Telegram Mini-App: detect and authenticate before mounting the app.
 *
 * When opened inside Telegram, performs auth via initData:
 * - success → exchanges token, stores authToken (router guard redirects to /dashboard)
 * - sponsor_required → stores state for SponsorModal to pick up
 * - totp_required → stores state for TotpModal to pick up
 *
 * Provides reactive state via app.provide() for injection in components:
 * - 'tmaState' — current TMA flow state
 * - 'isInTelegram' — whether running inside Telegram
 */
const tmaState = ref<TmaState>({
  status: 'idle',
  initData: '',
  authResult: null,
  errorMessage: '',
  abortedToLanding: false,
})
const tmaAuthFlow = createTmaAuthFlow(tmaState, key => i18n.global.t(key), path => { void router.replace(path) })

// Only launch data identifies a Mini App; neither a URL path nor a long-lived
// tma_user cookie may turn an ordinary browser into a Telegram login attempt.
async function handleTelegramMiniAppAuth(): Promise<void> {
  const initData = telegramInitData
  if (!initData) {
    tmaAddBreadcrumb('main.ts: no initData, skipping auth', {}, 'warning')
    tmaState.value.status = 'error'
    tmaState.value.errorMessage = i18n.global.t('tma.authError')
    return
  }

  // SDK is optional for authentication and may arrive after the bundle.
  const prepareTelegramWindow = () => {
    // Native window controls are best-effort and must never block authentication.
    try {
      window.Telegram?.WebApp?.ready()
      window.Telegram?.WebApp?.expand()
      window.Telegram?.WebApp?.disableVerticalSwipes?.()
    } catch {
      setTimeout(() => {
        try { window.Telegram?.WebApp?.disableVerticalSwipes?.() } catch { /* ignore */ }
      }, 500)
    }
  }
  if (window.Telegram?.WebApp) prepareTelegramWindow()
  else {
    // Never load Telegram's CDN on the ordinary website. Even in a Mini App,
    // a blocked CDN must not delay either authentication or page rendering.
    const telegramScript = document.createElement('script')
    telegramScript.src = 'https://telegram.org/js/telegram-web-app.js'
    telegramScript.async = true
    telegramScript.addEventListener('load', prepareTelegramWindow, { once: true })
    document.head.append(telegramScript)
  }

  await tmaAuthFlow.authenticate(initData)
  // Initial navigation ran before exchange; storing a token does not rerun guards.
  if (tmaState.value.status === 'done') await router.replace('/dashboard')
}

// Provide TMA state to all components
app.provide('tmaState', tmaState)
app.provide('tmaAuthFlow', tmaAuthFlow)
app.provide('isInTelegram', isInTelegram)

// Mount and hide skeleton after Vue is ready
router.isReady().then(async () => {
  // Ensure localized messages are loaded before mounting
  await loadLocale(getCurrentLocale())

  // Telegram Mini-App: authenticate before mount to prevent landing page flash
  if (isInTelegram) {
    document.body.classList.add('tma-loading')
    // Update skeleton text for TMA context
    const loadingEl = document.getElementById('skeleton-loading-text')
    if (loadingEl) {
      const i18n = window.__skeletonI18n
      loadingEl.textContent = i18n?.locale === 'ru' ? 'Авторизация...' : 'Authenticating...'
    }

    await handleTelegramMiniAppAuth()
    if (tmaState.value.abortedToLanding) await router.replace('/')
    else if (tmaState.value.status !== 'done') await router.replace('/tma-init')
    document.body.classList.remove('tma-loading')
  }

  const mountEl = document.getElementById('gonkagame-app')

  if (mountEl) {
    // Ensure the container is empty to prevent hydration mismatches
    mountEl.innerHTML = ''
    app.mount(mountEl)

    // Use requestAnimationFrame to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      // Clear the skeleton timeout since app loaded successfully
      if (window.__appLoadTimeout) {
        clearTimeout(window.__appLoadTimeout)
        delete window.__appLoadTimeout
      }

      const skeleton = document.getElementById('app-skeleton')
      if (skeleton) {
        window.__mascotBootLoader?.complete()
        setTimeout(() => {
          skeleton.classList.add('hidden')
          setTimeout(() => {
            skeleton.remove()
            delete window.__mascotBootLoader
          }, 300)
        }, 240)
      }
    })
  } else {
    console.error('[GonkaGame] Mount element #gonkagame-app not found')
  }
})
